import { useState, useEffect, useMemo } from 'react';
import { X } from 'lucide-react';
import api, { integrationApi } from '../../lib/api';
import toast from 'react-hot-toast';
import type { BlastSportSummary, Gender, Season, SportWithTeams } from '../../types';
import { getAvailableBlastSports, getDisabledGenders } from '../../lib/sportFilters';
import { formatSportLabel } from '../../lib/sportLabel';

interface AddSportModalProps {
  isOpen: boolean;
  onClose: () => void;
  organizationId: string;
  organizationName: string;
  existingSports: SportWithTeams[];
  onSuccess: (sport: SportWithTeams) => void;
}

// Local fallback list shown only when /api/integration/sports is unreachable.
const SPORT_SUGGESTIONS = [
  'Football',
  'Basketball',
  'Baseball',
  'Softball',
  'Soccer',
  'Volleyball',
  'Track & Field',
  'Cross Country',
  'Swimming',
  'Wrestling',
  'Tennis',
  'Golf',
  'Lacrosse',
  'Field Hockey',
  'Cheerleading',
];

const GENDER_OPTIONS: { value: Gender; label: string }[] = [
  { value: 'BOYS', label: 'Boys' },
  { value: 'GIRLS', label: 'Girls' },
  { value: 'COED', label: 'Co-Ed' },
];

const SEASON_OPTIONS: { value: Season; label: string }[] = [
  { value: 'FALL', label: 'Fall' },
  { value: 'WINTER', label: 'Winter' },
  { value: 'SPRING', label: 'Spring' },
  { value: 'SUMMER', label: 'Summer' },
];

const ALL_GENDERS: Gender[] = ['BOYS', 'GIRLS', 'COED'];

export const AddSportModal = ({
  isOpen,
  onClose,
  organizationId,
  organizationName,
  existingSports,
  onSuccess,
}: AddSportModalProps) => {
  const [name, setName] = useState('');
  const [gender, setGender] = useState<Gender>('BOYS');
  const [season, setSeason] = useState<Season>('FALL');
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [blastSports, setBlastSports] = useState<BlastSportSummary[]>([]);
  const [isLoadingBlastSports, setIsLoadingBlastSports] = useState(false);
  const [blastSportsFallback, setBlastSportsFallback] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setName('');
    setGender('BOYS');
    setSeason('FALL');
    setError(null);
    setBlastSportsFallback(false);
    setIsLoadingBlastSports(true);

    integrationApi
      .getBlastSports()
      .then((sports) => {
        setBlastSports(sports);
        setBlastSportsFallback(false);
      })
      .catch(() => {
        setBlastSports([]);
        setBlastSportsFallback(true);
      })
      .finally(() => setIsLoadingBlastSports(false));
  }, [isOpen]);

  const availableSportOptions = useMemo<BlastSportSummary[]>(() => {
    if (blastSportsFallback) {
      return SPORT_SUGGESTIONS.map((n) => ({
        blastSportId: -1,
        name: n,
        season: null as Season | null,
      }));
    }
    return getAvailableBlastSports(blastSports, existingSports);
  }, [blastSports, blastSportsFallback, existingSports]);

  const disabledGenders = useMemo(
    () => getDisabledGenders(existingSports, name),
    [existingSports, name],
  );

  // Nudge `gender` to a non-disabled option when the user picks a sport whose
  // currently-selected gender is already taken.
  useEffect(() => {
    if (!name) return;
    if (!disabledGenders.has(gender)) return;
    const next = ALL_GENDERS.find((g) => !disabledGenders.has(g));
    if (next) setGender(next);
  }, [name, disabledGenders, gender]);

  // Pre-fill season when a BlastSport with a known season is selected.
  useEffect(() => {
    if (!name) return;
    const match = availableSportOptions.find(
      (s) => s.name.toLowerCase() === name.trim().toLowerCase(),
    );
    if (match?.season) setSeason(match.season);
  }, [name, availableSportOptions]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!name.trim()) {
      setError('Sport name is required');
      return;
    }

    setIsSaving(true);
    setError(null);

    try {
      const response = await api.post(
        `/api/organizations/${organizationId}/sports`,
        { name: name.trim(), gender, season }
      );

      toast.success(`Added ${formatSportLabel(gender, name.trim())}`);
      onSuccess(response.data.sport);
      onClose();
    } catch (err: unknown) {
      const e = err as { response?: { status?: number; data?: { message?: string } } };
      const message = e.response?.data?.message || 'Failed to create sport';
      if (e.response?.status === 409) {
        setError('This sport already exists at this organization');
      } else {
        setError(message);
      }
    } finally {
      setIsSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div
        className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={onClose}
      />
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full">
          <form onSubmit={handleSubmit}>
            <div className="border-b border-gray-200 dark:border-gray-700 px-6 py-4 flex items-center justify-between">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
                Add Sport
              </h2>
              <button
                type="button"
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Adding sport to <span className="font-medium text-gray-900 dark:text-gray-100">{organizationName}</span>
              </p>

              {error && (
                <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg p-3 text-red-800 dark:text-red-300 text-sm">
                  {error}
                </div>
              )}

              {/* Sport name — true dropdown (no free-form entry; sports must come from the BlastSport list). */}
              <div>
                <label htmlFor="sport-name" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Sport Name <span className="text-red-500">*</span>
                </label>
                <select
                  id="sport-name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="input"
                  disabled={isSaving || isLoadingBlastSports}
                  autoFocus
                >
                  <option value="" disabled>
                    {isLoadingBlastSports ? 'Loading sports…' : '— Select a sport —'}
                  </option>
                  {availableSportOptions.map((s) => (
                    <option key={`${s.name}-${s.blastSportId}`} value={s.name}>
                      {s.name}
                    </option>
                  ))}
                </select>
                {blastSportsFallback && (
                  <p className="mt-1 text-xs text-amber-600 dark:text-amber-400">
                    Using local list — Blast sport directory unavailable.
                  </p>
                )}
                {!blastSportsFallback && !isLoadingBlastSports && availableSportOptions.length === 0 && (
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    All Blast sports already added to this organization.
                  </p>
                )}
              </div>

              {/* Gender */}
              <div>
                <label htmlFor="sport-gender" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Gender <span className="text-red-500">*</span>
                </label>
                <select
                  id="sport-gender"
                  value={gender}
                  onChange={(e) => setGender(e.target.value as Gender)}
                  className="input"
                  disabled={isSaving}
                >
                  {GENDER_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value} disabled={disabledGenders.has(opt.value)}>
                      {opt.label}
                      {disabledGenders.has(opt.value) ? ' (already added)' : ''}
                    </option>
                  ))}
                </select>
              </div>

              {/* Season */}
              <div>
                <label htmlFor="sport-season" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Season <span className="text-red-500">*</span>
                </label>
                <select
                  id="sport-season"
                  value={season}
                  onChange={(e) => setSeason(e.target.value as Season)}
                  className="input"
                  disabled={isSaving}
                >
                  {SEASON_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="bg-gray-50 dark:bg-gray-800/50 border-t border-gray-200 dark:border-gray-700 px-6 py-4 flex justify-end space-x-3">
              <button
                type="button"
                onClick={onClose}
                className="btn-secondary"
                disabled={isSaving}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn-primary"
                disabled={isSaving || !name.trim()}
              >
                {isSaving ? (
                  <>
                    <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></span>
                    Adding...
                  </>
                ) : (
                  'Add Sport'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};
